/* eslint-disable */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const FloatingDiscountTimer = ({
    show = false,
    deadline,
    // ✅ tu peux utiliser start/end (recommandé)
    // position = { top: "180px", start: 0 }   // LTR => left, RTL => right
    position = { top: "180px", start: 0 },
    dir = "rtl",
    onCountdownClick,
    onNavigate,
}) => {
    const { t } = useTranslation();
    const isRTL = dir === "rtl";
    const [isExpanded, setIsExpanded] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsExpanded(false);
            }
        };

        if (isExpanded) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('touchstart', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [isExpanded]);

    const getOuterRounded = (anchoredSide) => {
        if (isRTL) {
            if (anchoredSide === "right") return "rounded-l-2xl";
            return "rounded-r-2xl";
        }
        if (anchoredSide === "left") return "rounded-r-2xl";
        return "rounded-l-2xl";
    };

    const defaultDeadlineRef = useRef(
        new Date(new Date().getFullYear(), 2, 1, 0, 0, 0)
    );
    const effectiveDeadline = deadline ?? defaultDeadlineRef.current;

    const parsedDeadline = useMemo(() => {
        const base =
            effectiveDeadline instanceof Date
                ? effectiveDeadline
                : new Date(effectiveDeadline);

        if (Number.isNaN(base.getTime())) return null;

        const now = new Date();
        if (base.getTime() <= now.getTime()) {
            return new Date(
                base.getFullYear() + 1,
                base.getMonth(),
                base.getDate(),
                base.getHours(),
                base.getMinutes(),
                base.getSeconds()
            );
        }
        return base;
    }, [effectiveDeadline]);

    const [timeLeft, setTimeLeft] = useState({
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
    });
    const pad = (value) => String(value).padStart(2, "0");

    useEffect(() => {
        if (!parsedDeadline) return undefined;

        const tick = () => {
            const diff = parsedDeadline.getTime() - Date.now();
            const clamped = Math.max(0, diff);
            const days = Math.floor(clamped / (1000 * 60 * 60 * 24));
            const hours = Math.floor((clamped / (1000 * 60 * 60)) % 24);
            const minutes = Math.floor((clamped / (1000 * 60)) % 60);
            const seconds = Math.floor((clamped / 1000) % 60);
            setTimeLeft({ days, hours, minutes, seconds });
        };

        tick();
        const timer = setInterval(tick, 1000);
        return () => clearInterval(timer);
    }, [parsedDeadline]);

    if (!show || !parsedDeadline) return null;

    const labelDays = t("floatingTimer.days", "jours");
    const limitedOfferLabel = t("floatingTimer.limitedOffer", "Offre limitée");
    const addReceiptLabel = t("floatingTimer.addReceipt", "Ajouter le reçu");
    const ariaOpenReceipt = t(
        "floatingTimer.ariaOpenReceipt",
        "Ouvrir la validation du reçu"
    );
    const ariaExpiry = t(
        "floatingTimer.ariaExpiry",
        "Offre limitée — expire dans {{days}} jours et {{hours}} heures",
        { days: timeLeft.days, hours: timeLeft.hours }
    );

    const hh = pad(timeLeft.hours);
    const mm = pad(timeLeft.minutes);
    const ss = pad(timeLeft.seconds);

    const resolveSidePosition = () => {
        const top = position?.top ?? "180px";

        const hasStart = position && Object.prototype.hasOwnProperty.call(position, "start");
        const hasEnd = position && Object.prototype.hasOwnProperty.call(position, "end");

        if (hasStart || hasEnd) {
            const startVal = hasStart ? position.start : undefined;
            const endVal = hasEnd ? position.end : undefined;

            const left = isRTL ? endVal : startVal;
            const right = isRTL ? startVal : endVal;

            const anchoredSide =
                (left != null && left !== "auto") ? "left" : (right != null && right !== "auto") ? "right" : (!isRTL ? "right" : "left");

            return { top, left, right, anchoredSide };
        }

        const left = position?.left;
        const right = position?.right;

        const anchoredSide =
            (left != null && left !== "auto") ? "left" : (right != null && right !== "auto") ? "right" : (isRTL ? "right" : "left");

        return { top, left: left ?? "auto", right: right ?? "auto", anchoredSide };
    };

    const { top, left, right, anchoredSide } = resolveSidePosition();

    const slideAnim = anchoredSide === "left" ? "slideInFromLeft" : "slideInFromRight";

    const hoverTranslateX = anchoredSide === "left" ? 4 : -4;

    const roundedSide = getOuterRounded(anchoredSide);

    const mobileRadius =
        anchoredSide === "left"
            ? "0 999px 999px 0"
            : "999px 0 0 999px";

    return (
        <div style={{ position: "relative", zIndex: 1000 }} ref={containerRef}>
            <div
                id="floating-discount-toast"
                className={`fixed z-50 group mobile-timer-container ${isExpanded ? 'timer-expanded' : ''} ${isCollapsed ? 'timer-collapsed' : ''}`}
                style={{
                    top,
                    left: left ?? "auto",
                    right: right ?? "auto",
                    animation: `${slideAnim} 0.7s cubic-bezier(0.16, 1, 0.3, 1) forwards`,
                }}
                dir={dir}
                role="alert"
                aria-live="polite"
                aria-label={ariaExpiry}
            >
                <button
                    type="button"
                    onClick={(e) => {
                        if (window.innerWidth <= 768 && !isExpanded) {
                            e.preventDefault();
                            setIsExpanded(true);
                        } else {
                            onNavigate?.('/special-offer');
                        }
                    }}
                    className={`relative flex items-stretch overflow-hidden ${roundedSide} desktop-full-timer focus:outline-none focus-visible:ring-2 focus-visible:ring-red-300`}
                    style={{
                        cursor: onCountdownClick ? "pointer" : "default",
                        boxShadow:
                            "0 8px 32px rgba(220, 38, 38, 0.45), 0 2px 8px rgba(0,0,0,0.18)",
                        background: "#dc2626",
                        border: "none",
                        transition: "transform 0.2s ease, box-shadow 0.2s ease",
                        flexDirection: !isRTL ? "row-reverse" : "row",
                    }}
                    dir="ltr"
                    onMouseEnter={(e) => {
                        e.currentTarget.style.transform = `translateX(${hoverTranslateX}px) scale(1.03)`;
                        e.currentTarget.style.boxShadow =
                            "0 12px 40px rgba(220, 38, 38, 0.6), 0 4px 12px rgba(0,0,0,0.22)";
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateX(0) scale(1)";
                        e.currentTarget.style.boxShadow =
                            "0 8px 32px rgba(220, 38, 38, 0.45), 0 2px 8px rgba(0,0,0,0.18)";
                    }}
                    aria-label={ariaOpenReceipt}
                >
                    {!isCollapsed && (
                        <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setIsExpanded(false);
                                setIsCollapsed(true);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setIsExpanded(false);
                                    setIsCollapsed(true);
                                }
                            }}
                            className="absolute z-10 w-6 h-6 text-gray-400 hover:text-gray-500 font-bold flex items-center justify-center"
                            style={{
                                top: "6px",
                                right: "6px",
                                lineHeight: 1,
                                background: "transparent",
                            }}
                            aria-label={t("ui.closeTimer")}
                        >
                            ×
                        </span>
                    )}
                    <div
                        className="flex flex-col items-center justify-center px-4 py-3"
                        style={{ background: "#ffffff", minWidth: "130px" }}
                    >
                        <div className="flex items-center gap-1 mb-1.5">
                            <span
                                style={{
                                    fontSize: "9px",
                                    color: "#dc2626",
                                    fontWeight: 700,
                                    letterSpacing: "0.12em",
                                    textTransform: "uppercase",
                                }}
                            >
                                ⚡ {limitedOfferLabel}
                            </span>
                        </div>

                        <div
                            className="flex items-center gap-0.5"
                            style={{ fontFamily: "'Courier New', Courier, monospace" }}
                        >
                            <TimeBlock value={hh} />
                            <Colon />
                            <TimeBlock value={mm} />
                            <Colon />
                            <TimeBlock value={ss} animated />
                        </div>

                        <span
                            className="mt-1.5"
                            style={{
                                fontSize: "7px",
                                color: "#6b7280",
                                letterSpacing: "0.08em",
                                textTransform: "uppercase",
                            }}
                        >
                            {addReceiptLabel}
                        </span>
                    </div>

                    <div
                        style={{
                            width: "2px",
                            background: "rgba(220,38,38,0.15)",
                            flexShrink: 0,
                        }}
                    />

                    <div
                        className="flex items-center justify-center"
                        style={{ background: "#dc2626", padding: "12px 14px" }}
                    >
                        <div
                            style={{
                                width: "58px",
                                height: "58px",
                                borderRadius: "50%",
                                background: "#ffffff",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                boxShadow:
                                    "0 0 0 3px rgba(255,255,255,0.3), inset 0 1px 3px rgba(0,0,0,0.08)",
                                flexShrink: 0,
                            }}
                        >
                            <span
                                style={{
                                    fontSize: "22px",
                                    fontWeight: 800,
                                    color: "#dc2626",
                                    lineHeight: 1,
                                    fontFamily: "'Courier New', Courier, monospace",
                                    letterSpacing: "-1px",
                                }}
                            >
                                {pad(timeLeft.days)}
                            </span>
                            <span
                                style={{
                                    fontSize: "7px",
                                    color: "#9ca3af",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.1em",
                                    marginTop: "2px",
                                    fontWeight: 600,
                                }}
                            >
                                {labelDays}
                            </span>
                        </div>
                    </div>

                    <div
                        className="absolute inset-0 pointer-events-none"
                        style={{
                            background:
                                "linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.18) 50%, transparent 60%)",
                            transform: "translateX(-100%)",
                            transition: "transform 0s",
                        }}
                    />
                </button>

                <div className="mobile-pill-only">
                    <button
                        type="button"
                        onClick={(e) => {
                            e.preventDefault();
                            if (window.innerWidth <= 768) {
                                setIsExpanded(true);
                            } else {
                                setIsCollapsed(false);
                            }
                        }}
                        style={{
                            background: "#dc2626",
                            border: "none",
                            borderRadius: mobileRadius,
                            padding: "8px 10px 8px 8px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: "0 4px 16px rgba(220,38,38,0.5)",
                            cursor: onCountdownClick ? "pointer" : "default",
                        }}
                        aria-label={ariaOpenReceipt}
                    >
                        <div
                            style={{
                                width: "46px",
                                height: "46px",
                                borderRadius: "50%",
                                background: "#ffffff",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <span
                                style={{
                                    fontSize: "17px",
                                    fontWeight: 800,
                                    color: "#dc2626",
                                    lineHeight: 1,
                                }}
                            >
                                {pad(timeLeft.days)}
                            </span>
                            <span
                                style={{
                                    fontSize: "6px",
                                    color: "#9ca3af",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.08em",
                                }}
                            >
                                {labelDays}
                            </span>
                        </div>
                    </button>
                </div>

                <style
                    dangerouslySetInnerHTML={{
                        __html: `
              @keyframes slideInFromLeft {
                from { transform: translateX(-110%); opacity: 0; }
                to   { transform: translateX(0); opacity: 1; }
              }
              @keyframes slideInFromRight {
                from { transform: translateX(110%); opacity: 0; }
                to   { transform: translateX(0); opacity: 1; }
              }
              @keyframes secondTick {
                0% { transform: scale(1); }
                10% { transform: scale(1.12); color: #dc2626; }
                20% { transform: scale(1); }
              }

              /* Default logic - hide pill on desktop */
              .mobile-pill-only {
                display: none;
              }

                            #floating-discount-toast.timer-collapsed > .desktop-full-timer {
                                position: absolute;
                                opacity: 0;
                                visibility: hidden;
                                transform: scale(0.6);
                                pointer-events: none;
                            }

                            #floating-discount-toast.timer-collapsed .mobile-pill-only {
                                display: block;
                                opacity: 1;
                                visibility: visible;
                                transform: scale(1);
                                position: relative;
                            }

              @media (max-width: 768px) {
                #floating-discount-toast {
                  display: flex;
                  align-items: center;
                }

                #floating-discount-toast > .desktop-full-timer { 
                  position: absolute;
                  opacity: 0;
                  visibility: hidden;
                  transform: scale(0.6);
                  transform-origin: ${anchoredSide} center;
                  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
                  pointer-events: none;
                }
                
                .mobile-pill-only { 
                  display: block;
                  opacity: 1;
                  visibility: visible;
                  transform: scale(1);
                  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
                  position: relative;
                }

                /* Expanded state for mobile */
                #floating-discount-toast.timer-expanded > .desktop-full-timer { 
                  position: relative;
                  opacity: 1;
                  visibility: visible;
                  transform: scale(0.85);
                  pointer-events: auto;
                }
                
                #floating-discount-toast.timer-expanded .mobile-pill-only { 
                  position: absolute;
                  opacity: 0;
                  visibility: hidden;
                  transform: scale(0.6);
                  pointer-events: none;
                }
              }
            `,
                    }}
                />
            </div>
        </div>
    );
};

const TimeBlock = ({ value, animated }) => (
    <div style={{ display: "flex", gap: "1px" }}>
        {value.split("").map((digit, i) => (
            <span
                key={i}
                style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "22px",
                    height: "28px",
                    background: animated ? "#fef2f2" : "#f9fafb",
                    borderRadius: "5px",
                    fontSize: "16px",
                    fontWeight: 800,
                    color: "#111827",
                    border: "1px solid #f3f4f6",
                    animation: animated ? "secondTick 1s ease-in-out infinite" : "none",
                    animationDelay: animated ? `${i * 0.05}s` : "0s",
                }}
            >
                {digit}
            </span>
        ))}
    </div>
);

const Colon = () => (
    <span
        style={{
            fontSize: "18px",
            fontWeight: 700,
            color: "#d1d5db",
            margin: "0 1px",
            lineHeight: 1,
            alignSelf: "center",
            marginBottom: "2px",
        }}
    >
        :
    </span>
);

export default FloatingDiscountTimer;